import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { COVERS } from '../lib/media';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Home() {
  return (
    <>
      {/* Hero */}
      <section className="hero">
        <HeroVideo className="video-bg" src={VIDEOS.meatHero} />
        <div className="video-overlay" />
        <div className="container home-hero-container">
          <div
            className="hero-content text-center"
          >
            <span className="hero-label">Fasting Carnivore</span>
            <p className="hero-pre-headline">What if eating was simple again?</p>
            <h1>The Future Is Coming.<br />Show Up Ready. Thrive Together.</h1>
            <p>
              No more confusion. No more second-guessing. Just a clear path forward—and people who are walking it with you.
            </p>
            <Link to="/join" className="btn btn-primary btn-lg">
              Join the Community
            </Link>
          </div>
        </div>
      </section>

      {/* The Struggle */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center mb-8">
                You've Tried Everything
              </h2>
              <p className="text-lead mb-responsive-lg">
                Low-fat. Low-carb. Keto. Paleo. Counting calories. Tracking macros. Meal plans that lasted a week before life got in the way.
              </p>
              <p className="text-lead mb-responsive-lg">
                You've read the books. Watched the documentaries. Followed the experts. And maybe some of it worked—for a while. But something always pulled you back.
              </p>
              <p className="text-lead mb-responsive-lg">
                You've found something that works.
              </p>
              <p className="text-lead mb-responsive-lg">
                Maybe you've already started carnivore. Maybe you're just curious.
                Either way, you know there's something to this. Something that makes sense
                in a way other approaches never did.
              </p>
              <p className="text-lead mb-responsive-lg">
                But here's what nobody talks about:
              </p>
              <p className="text-lead text-emphasis">
                Doing it alone is the hardest part. That's why finding your tribe matters.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Pain */}
      <section className="section section-dark">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                The Looks. The Questions. The Doubt.
              </h2>
              <p className="text-lead mb-responsive-lg text-mid-gray">
                You're eating differently. And suddenly everyone has an opinion.
              </p>
              <p className="text-lead mb-responsive-lg text-mid-gray">
                Family dinners where you have to explain yourself. Again.
                Friends who think you've lost your mind.
                Coworkers with opinions about what's on your plate.
              </p>
              <p className="text-lead mb-responsive-lg text-mid-gray">
                And the worst part? The doubt that creeps in when you're the only one.
                When you have a question at 2am and no one to ask.
                When you wonder if maybe everyone else is right.
              </p>
              <p className="text-lead mb-responsive-lg text-mid-gray">
                You start strong. But without support, the world pulls you back. The convenience of the old way. The pressure to "just have a little." The exhaustion of constantly swimming against the current.
              </p>
              <p className="text-lead text-emphasis-light">
                The difference between trying and thriving? Having people in your corner who get it.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                What If You Had People Who Get It?
              </h2>
              <p className="text-lead mb-responsive-lg">
                People who don't need an explanation. Who celebrate your wins without
                the skeptical looks. Who've been exactly where you are.
              </p>
              <p className="text-lead mb-responsive-lg">
                People who answer your questions because they've had the same ones.
                Who make you feel normal instead of weird.
                Who are there at 2am when you need them.
              </p>
              <p className="text-lead mb-responsive-lg">
                Imagine having a place to go where everyone eats the way you do. Where your wins are celebrated. Where your struggles are understood. Where you can ask any question without judgment.
              </p>
              <p className="text-lead text-emphasis">
                That's what community is. That's what changes everything. That's what makes this sustainable for life.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="section section-light">
        <div className="container">
          <motion.div
            className="content-narrow quote-block text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-quote mb-responsive text-dark">
              "I tried doing this on my own for months. It wasn't until I found this community that everything clicked. The community is why I'm still doing this. Without them, I would have quit months ago."
            </p>
            <p className="quote-attribution">
              — David K., member since 2023
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Books */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="home-books-header"
          >
            <span className="hero-label">The Library</span>
            <h2 className="heading-section">The Complete Toolkit</h2>
            <p className="text-lead text-muted">
              Four books. One lifestyle. Everything you need to understand, apply, and thrive with the fasting carnivore approach—without the confusion or guesswork.
            </p>
          </motion.div>

          <div className="home-books-grid">
            <FeaturedBook
              title="Fasting Carnivore"
              slug="fasting-carnivore"
              image={COVERS.carnivore}
              description="The foundational stories and science. Understand why this works and how to make it work for you."
            />
            <FeaturedBook
              title="Nano Fasting"
              slug="fasting-carnivore"
              image={COVERS.nanoFasting}
              description="The effortless approach to extended fasting. No willpower required. Just a simple shift in how you think about eating."
            />
            <FeaturedBook
              title="Performance"
              slug="performance"
              image={COVERS.performance}
              description="For those who move. Training, recovery, and athletic performance on a meat-based diet."
            />
            <FeaturedBook
              title="Kitchen"
              slug="kitchen"
              image={COVERS.kitchen}
              description="Simple, satisfying recipes that make eating this way enjoyable—not boring."
            />
          </div>

          <div className="home-books-cta">
            <Link to="/programs" className="btn btn-outline btn-lg">
              Explore All Programs
            </Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                Your Tribe Is Waiting
              </h2>
              <p className="text-lead mb-responsive-lg text-center">
                Thousands of people are already doing this together. They've built something special—and there's room for you.
              </p>
              <p className="text-lead mb-responsive-lg">
                They share their wins. They ask their questions. They support each other
                through the hard days and celebrate the breakthroughs. No judgment. No gatekeeping. Just people helping people.
              </p>
              <p className="text-lead mb-responsive-lg">
                It's a safe space where people get it. Where you belong.
                Where you're celebrated, not questioned. Where your way of eating is the norm, not the exception.
              </p>
              <p className="text-lead text-emphasis">
                When you join, you get everything—the community, the books, the guides, the support.
                But more importantly, you get your people. And that's what makes all the difference.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Second Testimonial */}
      <section className="section section-dark">
        <div className="container">
          <motion.div
            className="content-narrow text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-quote mb-responsive text-emphasis-light">
              "I was struggling for weeks. Couldn't figure out what was wrong. Someone answered my question at 3am. That's when I knew I'd found my tribe."
            </p>
            <p className="quote-attribution-dark">
              — Jennifer R., member since 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* This is for you */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-subsection">
                This is for you if:
              </h2>
              <ul className="list-responsive text-dark-gray">
                <li>You're doing carnivore (or curious about starting) and don't want to figure it all out alone</li>
                <li>You're exhausted from explaining yourself to people who don't understand</li>
                <li>You want people in your corner who've walked the path you're on</li>
                <li>You know deep down that community makes the difference between giving up and going all in</li>
                <li>You're done with the confusion and ready to learn from people who've been there</li>
                <li>You're ready to stop feeling like the weird one and start feeling at home</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta py-responsive">
        <div className="container">
          <motion.div
            className="content-narrow text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-section">
              You Found Your Tribe
            </h2>
            <p className="text-subtitle mb-responsive-lg">
              Stop going it alone. Start thriving together.
            </p>
            <Link to="/join" className="btn btn-secondary btn-lg">
              Join the Community Now
            </Link>
            <p className="home-cta-footnote">
              $97 one-time · Lifetime access · All books included
            </p>
          </motion.div>
        </div>
      </section>

      {/* PS */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-body-lg text-muted">
                <strong className="text-dark">P.S.</strong> — You already know this way of eating makes sense.
                The hardest part isn't the food—it's doing it alone. That's what we fix.
                Join thousands of people who are learning, growing, and thriving together. Your tribe is waiting.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}

function FeaturedBook({ title, image, description }: { title: string; slug?: string; image: string; description: string }) {
  return (
    <Link to={`/programs`} className="featured-book-card">
      <div className="featured-book-image-wrapper">
        <img src={image} alt={title} className="featured-book-image" />
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
    </Link>
  );
}
